import React from "react";
import {
  Button,
  InlineEditor,
  Modal,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import { updateProjectDescriptionSagaAction } from "../../../../../../redux/saga/projects/saga-actions";
import { useTimelineStepPermissions } from "../../../../../../hooks/useTimelineStepPermissions";
import { notesToolBarConfig } from "../validation/components/NotesSection";
import ProjectDetailsEditIcon from "./ProjectDetailsEditIcon";
import {
  DESCRIPTION_MAX_LENGTH,
  hasVisibleDescriptionContent,
  sanitizeDescriptionHtml,
  stripHtml,
  toDescriptionDisplayHtml,
} from "./descriptionEditorHelpers";
import "./DescriptionSection.scss";

type DescriptionSectionProps = {
  technologyId: number;
  description?: string | null;
};

const DescriptionSection: React.FC<DescriptionSectionProps> = ({
  technologyId,
  description,
}) => {
  const dispatch = useAppDispatch();
  const { canEditTimeline } = useTimelineStepPermissions();
  const { projectDescriptionUpdateLoading } = useAppSelector(
    (state) => state.projects.status
  );
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [descriptionDraft, setDescriptionDraft] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const hasDescription = hasVisibleDescriptionContent(description || "");
  const descriptionPlainLength = stripHtml(descriptionDraft);

  const openEditModal = () => {
    if (!canEditTimeline) return;
    setDescriptionDraft(description || "");
    setErrorMessage(null);
    setIsEditOpen(true);
  };

  const closeEditModal = () => {
    if (projectDescriptionUpdateLoading) return;
    setIsEditOpen(false);
    setErrorMessage(null);
  };

  const handleSave = () => {
    const sanitized = sanitizeDescriptionHtml(descriptionDraft);
    if (!stripHtml(sanitized)) {
      setErrorMessage("Description is required.");
      return;
    }

    dispatch(
      updateProjectDescriptionSagaAction(
        technologyId,
        { project_description: sanitized },
        () => {
          setIsEditOpen(false);
          setErrorMessage(null);
        },
        (message) => {
          setErrorMessage(message);
        }
      )
    );
  };

  return (
    <>
      <div className="description-section">
        <div className="description-section__header">
          <h3 className="description-section__title">Description</h3>
          {canEditTimeline ? (
            <button
              type="button"
              className="description-section__edit"
              onClick={openEditModal}
            >
              <ProjectDetailsEditIcon className="description-section__edit-icon" />
              Edit
            </button>
          ) : null}
        </div>

        <div className="description-section__body">
          <div className="description-section__text">
            {hasDescription ? (
              <div
                className="description-section__rich-text"
                dangerouslySetInnerHTML={{
                  __html: toDescriptionDisplayHtml(description || ""),
                }}
              />
            ) : (
              <p className="description-section__empty">
                No description available.
              </p>
            )}
          </div>
        </div>
      </div>

      {isEditOpen ? (
        <Modal
          headerText="Edit Description"
          width={720}
          onClose={closeEditModal}
          className="description-section__edit-modal"
        >
          <div className="description-section__edit-modal-content">
            <div className="description-section__edit-editor hid-notes-editor">
              <div className="description-section__edit-label-row">
                <label htmlFor="project_description_edit">Description</label>
                <span className="description-section__edit-char-count">
                  {descriptionPlainLength.length}/{DESCRIPTION_MAX_LENGTH}
                </span>
              </div>
              <InlineEditor
                id="project_description_edit"
                placeholder="Enter project description"
                toolBarConfig={notesToolBarConfig}
                editorData={descriptionDraft}
                handleOnChange={(nextValue: string) => {
                  if (stripHtml(nextValue).length > DESCRIPTION_MAX_LENGTH) return;
                  setDescriptionDraft(nextValue);
                  if (errorMessage) {
                    setErrorMessage(null);
                  }
                }}
              />
            </div>
            {errorMessage ? (
              <p className="description-section__edit-error">{errorMessage}</p>
            ) : null}
            <div className="description-section__edit-actions">
              <Button
                variant="secondary"
                label="Cancel"
                normalCase={false}
                disabled={projectDescriptionUpdateLoading}
                onClick={closeEditModal}
              />
              <Button
                variant="primary"
                label={projectDescriptionUpdateLoading ? "Saving..." : "Save"}
                normalCase={false}
                disabled={projectDescriptionUpdateLoading}
                onClick={handleSave}
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

export default DescriptionSection;
